import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("pending");
    const [attempts, setAttempts] = useState(0);
    const nav = useNavigate();
    const { refresh } = useAuth();

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const poll = async () => {
            for (let i = 0; i < 12 && !cancelled; i++) {
                try {
                    const r = await client.get(`/payments/status/${sessionId}`);
                    setAttempts(i + 1);
                    if (r.data.payment_status === "paid") {
                        setStatus("paid");
                        if (refresh) await refresh();
                        return;
                    }
                    if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
                        setStatus("failed");
                        return;
                    }
                } catch {}
                await new Promise((res) => setTimeout(res, 2000));
            }
            if (!cancelled) setStatus("timeout");
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId, refresh]);

    return (
        <div className="min-h-screen grid place-items-center bg-background text-foreground px-6" data-testid="payment-success-page">
            <div className="max-w-md w-full border border-border bg-card/40 p-10">
                {status === "pending" ? (
                    <>
                        <Loader2 className="w-8 h-8 text-primary animate-spin mb-6" />
                        <div className="font-display text-2xl">Confirming payment…</div>
                        <div className="text-sm text-muted-foreground mt-2">Stripe is finalizing your subscription. (Attempt {attempts}/12)</div>
                    </>
                ) : status === "paid" ? (
                    <>
                        <div className="w-10 h-10 border border-primary grid place-items-center mb-6">
                            <Check className="w-5 h-5 text-primary" />
                        </div>
                        <div className="overline">Success</div>
                        <div className="font-display text-3xl mt-2">Welcome to Pro.</div>
                        <p className="text-sm text-muted-foreground mt-4">
                            Custom logos, unlimited shares, scheduled weekly emails, and priority AI are now unlocked. Reload the app to see your new tier.
                        </p>
                        <button
                            onClick={() => { window.location.href = "/app/reports"; }}
                            data-testid="continue-to-app-btn"
                            className="mt-8 bg-primary text-primary-foreground px-6 py-3 font-medium hover:bg-primary/90 w-full"
                        >
                            Go to Reports →
                        </button>
                    </>
                ) : status === "failed" ? (
                    <>
                        <div className="overline text-destructive">Payment failed</div>
                        <div className="font-display text-2xl mt-2">We couldn't process that.</div>
                        <p className="text-sm text-muted-foreground mt-4">No charge was made. You can retry from the pricing page.</p>
                        <Link to="/pricing" data-testid="retry-link" className="mt-6 inline-block bg-primary text-primary-foreground px-6 py-3 font-medium">Try again</Link>
                    </>
                ) : (
                    <>
                        <div className="overline">Still processing</div>
                        <div className="font-display text-2xl mt-2">Taking longer than usual…</div>
                        <p className="text-sm text-muted-foreground mt-4">Your payment may still complete. Check billing status in the app.</p>
                        <Link to="/app" data-testid="goto-app-link" className="mt-6 inline-block border border-border px-6 py-3">Go to app</Link>
                    </>
                )}
            </div>
        </div>
    );
}
